<template>
  <el-row :gutter="16">
    <el-col v-if="showDateField" :span="12">
      <PDatePicker
        v-model="date"
        type="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col
      v-if="showCompanyField"
      :span="showDateField ? 12 : 24"
      style="margin-top: 2px"
    >
      <PSelect
        v-model="empresa"
        placeholder="Selecione uma empresa"
        label="Empresa"
        :options="empresasOpt"
        filterable
        clearable
        :disabled="!empresaIsEnabled"
      />
    </el-col>
    <el-col v-if="showPeriodSelector" :span="24" style="margin: 0.7rem 0">
      <PCheckbox
        v-model="selecionarPeriodo"
        label="Selecionar um periodo"
        :disabled="periodoForcado"
      />
    </el-col>
    <el-col v-if="selecionarPeriodo" :span="12" style="margin-bottom: 16px">
      <PDatePicker
        v-model="inicio"
        type="date"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de inicio"
        label="Inicio"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col v-if="selecionarPeriodo" :span="12" style="margin-bottom: 16px">
      <PDatePicker
        v-model="fim"
        type="date"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col
      v-if="metadataOptions.length"
      :span="24"
      class="formulario-integracao-base__metadata-select"
    >
      <PSelect
        v-model="metadata"
        :options="metadataOptions"
        :label="metadataLabel"
        placeholder="Selecione uma ou mais opções"
        multiple
        collapse-tags
        clearable
        filterable
        full
      />
    </el-col>
    <el-col
      v-if="metadataDescription.length"
      :span="24"
      class="formulario-integracao-base__metadata-help"
    >
      <p class="formulario-integracao-base__metadata-help-title">
        {{ metadataDescriptionTitle }}
      </p>
      <p
        v-for="(line, index) in metadataDescription"
        :key="index"
        class="formulario-integracao-base__metadata-help-line"
      >{{ line }}</p>
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import { TWebservice } from "../../../services/PainelDeIntegracaoesGateway";
import { useForm } from "vee-validate";
import { IFormularioBaseSchema, formBaseSchema } from "./formularioBaseSchema";
import { TAPIRules } from "../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import { DITypes } from "@/core/@types/types";
import { parseLocalDate } from "../composables/chamarServicoRequest";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
  DITypes.FINANCAS_FEATURES
);

const periodoForcado = computed(() => props.apiSelecionada?.forcesRange === true);
const showDateField = computed(() => props.apiSelecionada?.hideDateField !== true);
const showCompanyField = computed(
  () => props.apiSelecionada?.hideCompanyField !== true
);
const showPeriodSelector = computed(
  () =>
    props.apiSelecionada?.hidePeriodSelector !== true &&
    (!!props.apiSelecionada?.actionByRange || periodoForcado.value)
);
const metadataOptions = computed(() => props.apiSelecionada?.metadataOptions ?? []);
const metadataLabel = computed(
  () => props.apiSelecionada?.metadataLabel ?? "Quais ações deseja?"
);
const metadataDescriptionTitle = computed(
  () => props.apiSelecionada?.metadataDescriptionTitle ?? "O que são essas ações?"
);
const metadataDescription = computed(
  () => props.apiSelecionada?.metadataDescription ?? []
);

const formatDateInputValue = (value: Date): string => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDefaultDateValue = (): string => {
  const today = formatDateInputValue(new Date());
  const yesterday = parseLocalDate(today);

  yesterday.setDate(yesterday.getDate() - 1);

  return formatDateInputValue(yesterday);
};

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioBaseSchema>({
    validationSchema: formBaseSchema,
    initialValues: {
      date: getDefaultDateValue(),
      empresa: "",
      selecionarPeriodo: periodoForcado.value,
      inicio: null,
      fim: null,
      metadata: [],
      empresaIsEnabled: true
    }
  });

const [date] = defineField("date");
const [empresa] = defineField("empresa");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");
const [metadata] = defineField("metadata");
const [empresaIsEnabled] = defineField("empresaIsEnabled");

const campoEmpresaBloqueado = (): boolean => {
  return props.apiSelecionada?.companyEditable === false;
};

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";

  // TODO[FINC-1618]: mover obrigatoriedade de empresa para contrato do backend.
  if (showDateField.value && missingDate) {
    return true;
  }

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  if (!financasFeatureGateway) return;

  const response = await financasFeatureGateway.obterEmpresaPorConfiguracao(
    props.webservice?.id ?? ""
  );

  if (response?.Ok) {
    setValues({
      date: getDefaultDateValue(),
      empresa: response.Result,
      selecionarPeriodo: periodoForcado.value,
      inicio: null,
      fim: null,
      metadata: [],
      empresaIsEnabled: !response.Result
    });
  }
};

onMounted(async () => {
  if (props.apiSelecionada?.requiresCompanyLookup) {
    try {
      await obterEmpresaPorConfiguracao();
    } catch (error) {
      console.error(error);
    }
  } else {
    setValues({
      date: getDefaultDateValue(),
      empresa: "",
      selecionarPeriodo: periodoForcado.value,
      inicio: null,
      fim: null,
      metadata: [],
      empresaIsEnabled: !campoEmpresaBloqueado()
    });
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit,
  showCompanyField,
  showDateField,
  showPeriodSelector
});
</script>

<style lang="scss" scoped>
.formulario-integracao-base {
  &__metadata-select {
    margin-bottom: 16px;
  }

  &__metadata-help-title {
    margin: 0 0 4px;
    color: var(--financas-text-color-primary);
    font-size: 12px;
    font-weight: 600;
    line-height: 18px;
  }

  &__metadata-help-line {
    margin: 0;
    color: var(--financas-text-color-secondary);
    font-size: 12px;
    line-height: 18px;
  }

  &__metadata-help-line + &__metadata-help-line {
    margin-top: 4px;
  }
}
</style>
